import type { TemplateSection } from 'inspection-types';

import {
  offeredQuestionOperations,
  offeredSectionOperations,
  type QuestionPlacement,
  type SectionPlacement,
} from './offeredOperations';
import type { BlockedReason, VisibilityOperationKind } from './operations';
import {
  checkOperationPreconditions,
  isOperationAllowed,
  type PolicyContext,
} from './mutationPolicy';

// canHide and canUnhide say whether the entry is offered at all. A set blockedReason means
// the offered entry is refused right now and shows disabled with that reason.
export type OperationCapabilities = {
  canHide: boolean;
  canUnhide: boolean;
  blockedReason: BlockedReason | null;
};

// One entry per operation the user is offered. A set blockedReason means the entry is refused
// right now and shows disabled with that reason.
export type OperationCapability = {
  kind: VisibilityOperationKind;
  blockedReason: BlockedReason | null;
};

// canReorder says whether the drag handle is offered at all. It does not depend on where the
// node sits: questions under a hidden section reorder too.
export type ReorderCapability = {
  canReorder: boolean;
  blockedReason: BlockedReason | null;
};

const NO_CAPABILITIES: OperationCapabilities = {
  canHide: false,
  canUnhide: false,
  blockedReason: null,
};

const NO_REORDER_CAPABILITY: ReorderCapability = {
  canReorder: false,
  blockedReason: null,
};

function deriveCapabilities(
  kind: VisibilityOperationKind | undefined,
  targetId: string,
  ctx: PolicyContext
): OperationCapabilities {
  if (!kind) {
    return NO_CAPABILITIES;
  }

  const verdict = isOperationAllowed(kind, targetId, ctx);

  if (!verdict.allowed && verdict.reason === 'NOT_PERMITTED') {
    return NO_CAPABILITIES;
  }

  return {
    canHide: kind === 'hideQuestion' || kind === 'hideSection',
    canUnhide: kind === 'unhideQuestion' || kind === 'unhideSection',
    blockedReason: verdict.allowed ? null : verdict.reason,
  };
}

export function deriveQuestionCapabilities(
  placement: QuestionPlacement,
  ctx: PolicyContext
): OperationCapabilities {
  const [kind] = offeredQuestionOperations(placement);

  return deriveCapabilities(kind, placement.question.id, ctx);
}

function deriveOperationCapability(
  kind: VisibilityOperationKind,
  targetId: string,
  ctx: PolicyContext
): OperationCapability | null {
  const verdict = isOperationAllowed(kind, targetId, ctx);

  if (verdict.allowed) {
    return { kind, blockedReason: null };
  }

  if (verdict.reason === 'NOT_PERMITTED') {
    return null;
  }

  return { kind, blockedReason: verdict.reason };
}

export function deriveSectionCapabilities(
  section: SectionPlacement & Pick<TemplateSection, 'id'>,
  ctx: PolicyContext
): OperationCapability[] {
  const kinds = offeredSectionOperations(section);
  const capabilities: OperationCapability[] = [];

  for (const kind of kinds) {
    const capability = deriveOperationCapability(kind, section.id, ctx);

    if (capability) {
      capabilities.push(capability);
    }
  }

  return capabilities;
}

export function deriveReorderCapability(ctx: PolicyContext): ReorderCapability {
  const verdict = checkOperationPreconditions('reorder', ctx);

  if (verdict.allowed) {
    return { canReorder: true, blockedReason: null };
  }

  if (verdict.reason === 'NOT_PERMITTED') {
    return NO_REORDER_CAPABILITY;
  }

  return { canReorder: true, blockedReason: verdict.reason };
}
